import { DashboardOutlined } from '@ant-design/icons'
import { ConfigProvider, Menu, MenuProps } from 'antd'
import type { JSX } from 'react'
import { useHistory } from 'react-router'
import styled from 'styled-components'

import { ClientSectionGroup, OVERVIEW_SECTION } from '../clientSections'
import { MUTED_TEXT, NAV_HOVER_BG, NAV_SELECTED_BG, TEXT } from '../colors'

import { AttentionItem } from './Overview/health'
import { StatusBadge } from './StatusBadge'

import { route } from 'utils/router'

const MENU_THEME = {
  components: {
    Menu: {
      activeBarBorderWidth: 0,
      activeBarWidth: 0,
      groupTitleColor: MUTED_TEXT,
      groupTitleFontSize: 12,
      itemActiveBg: NAV_SELECTED_BG,
      itemBg: 'transparent',
      itemBorderRadius: 6,
      itemColor: TEXT,
      itemHeight: 38,
      itemHoverBg: NAV_HOVER_BG,
      itemHoverColor: TEXT,
      itemMarginBlock: 2,
      itemMarginInline: 0,
      itemSelectedBg: NAV_SELECTED_BG,
      itemSelectedColor: TEXT,
    },
  },
}

const StyledNav = styled.div`
  .ant-menu-root.ant-menu-inline {
    background: transparent;
    border-inline-end: none;
  }
  .ant-menu-item-group-title {
    padding: 18px 12px 6px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }
  .ant-menu-item {
    font-weight: 500;
  }
  .ant-menu-item-selected {
    font-weight: 600;
  }
  .ant-menu-title-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }
`

type SectionBadge = Pick<AttentionItem, 'badge' | 'tone'>

type ClientNavProps = {
  clientId: string
  groups: ClientSectionGroup[]
  selectedPath?: string
  attentionCount: number
  sectionBadges: Record<string, SectionBadge>
}

function NavLabel({ label, badge }: { label: string; badge?: SectionBadge }): JSX.Element {
  return (
    <>
      <span>{label}</span>
      {badge && <StatusBadge tone={badge.tone}>{badge.badge}</StatusBadge>}
    </>
  )
}

export function ClientNav({
  clientId,
  groups,
  selectedPath,
  attentionCount,
  sectionBadges,
}: ClientNavProps): JSX.Element {
  const history = useHistory()
  const toKey = (path: string) => route(path, { id: clientId })
  const hasAttentionError = Object.values(sectionBadges).some(({ tone }) => tone === 'error')

  const items: MenuProps['items'] = [
    {
      key: toKey(OVERVIEW_SECTION.path),
      icon: <DashboardOutlined />,
      label: (
        <NavLabel
          badge={
            attentionCount > 0
              ? { badge: attentionCount.toString(), tone: hasAttentionError ? 'error' : 'warning' }
              : undefined
          }
          label={OVERVIEW_SECTION.label}
        />
      ),
    },
    ...groups.map((group) => ({
      type: 'group' as const,
      key: group.label,
      label: group.label,
      children: group.sections.map((section) => ({
        key: toKey(section.path),
        label: <NavLabel badge={sectionBadges[section.path]} label={section.label} />,
      })),
    })),
  ]

  return (
    <ConfigProvider theme={MENU_THEME}>
      <StyledNav>
        <Menu
          aria-label="Client Sections"
          inlineIndent={12}
          items={items}
          mode="inline"
          onClick={({ key }) => history.push(key)}
          role="navigation"
          selectedKeys={selectedPath ? [toKey(selectedPath)] : []}
        />
      </StyledNav>
    </ConfigProvider>
  )
}
